/**
 * GLAMRDiP product-media video autoplay.
 * Joel 2026-04-29.
 *
 * Renders Shopify-uploaded product videos as GIF-style autoplay loops
 * in PDP galleries. Page-speed protocol enforced via:
 *
 *   - markup: preload="none" + poster = first frame → 0 bytes loaded
 *     until IO upgrades preload to "auto" + .load() on first intersect
 *   - IntersectionObserver fires play() at 50% visible, pause() when
 *     scrolled out → CPU/battery preserved on large galleries
 *   - prefers-reduced-motion: skip autoplay entirely, leave poster
 *
 * Defensive: try/catch around init so a single bad video never breaks
 * the rest of the page. Uses [data-gd-pdp-video] selector so any PDP
 * variant (kit, colour, liquid, accessory) opts in by adding the attr.
 */
(() => {
  'use strict';
  try {
    const videos = document.querySelectorAll('[data-gd-pdp-video]');
    if (!videos.length) return;

    const reducedMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      // Posters only — never load or play the videos.
      videos.forEach((v) => {
        v.removeAttribute('autoplay');
        try { v.pause(); } catch {}
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Ancient browsers — load + play immediately. PDPs rarely have
      // more than 1-2 videos so the perf cost is acceptable here.
      videos.forEach((v) => {
        v.preload = 'auto';
        try { v.load(); } catch {}
        v.play().catch(() => {});
      });
      return;
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (entry.isIntersecting) {
          // First-time visible: upgrade preload + load() so the
          // browser starts buffering. Then attempt play().
          if (v.preload === 'none') {
            v.preload = 'auto';
            try { v.load(); } catch {}
          }
          v.play().catch(() => {/* autoplay blocked → poster stays visible */});
        } else {
          try { v.pause(); } catch {}
        }
      });
    }, {
      threshold: 0.5,
      // Joel 2026-05-02: rootMargin bumped 50 → 500px so PDP video
      // metadata loads before the section enters viewport. Used by
      // kit-hero, nail-art how-to-use, drill tutorial, salon-kit
      // apply/remove, value-props, etc — site-wide consistency.
      rootMargin: '500px 0px',
    });

    videos.forEach((v) => io.observe(v));
  } catch (e) {
    console.warn('[gd-product-video] init failed:', e);
  }
})();
